import React, { useEffect, useRef, useState } from 'react';
import CalendarEmbed from './CalendarEmbed';
import FeedNoAvailable from './FeedNoAvailable';
import createTypeOfResource from 'components/Dashboard/helpers/createTypeOfResource';
import SkeletonKeyUpdates from 'components/Dashboard/KeyUpdates/SkeletonKeyUpdates';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import * as Sentry from '@sentry/react';
import ParliamentLiveItem from 'components/Dashboard/ParliamentLive/ParliamentLiveItem';
import ListItem from 'components/Dashboard/ListControl/ListItem';

const EmbedList = (props) => {
  const {
    results,
    typeOfContent,
    createFunctionOnClick,
    keywordListId,
    load_more_results,
    setIsLoading,
    apiCallPerContent,
    setResults,
    isLoading,
    scrollbarContainer,
    background,
    topic,
    userId,
    includeBrandingProperty,
    switchingResultsState,
  } = props;
  const { switchingResults, setSwitchingResults } = switchingResultsState ?? {};
  const switchingResource = useRef(false);

  const resultsToUse = results;
  const elementsToRender =
    typeOfContent === 'ParliamentLive'
      ? resultsToUse?.hits?.filter((item) => !item?.freshItem)?.slice(0, 100)
      : resultsToUse?.hits;

  const [freshItemsHeight, setFreshItemsHeight] = useState(0);
  const [showItems, setShowItems] = useState(false);
  const freshItemsRef = useRef(null);
  const freshElements = resultsToUse?.hits?.filter((item) => item.freshItem === true);

  const transitionDuration = freshElements?.length >= 15 ? 1000 : 500;
  const parliamentLiveSection = typeOfContent === 'RealtimeUtterances';

  const showNewItems = () => {
    setShowItems(true);
  };

  useEffect(() => {
    if (freshItemsRef.current) {
      setFreshItemsHeight(freshItemsRef.current.getBoundingClientRect().height);
    }
  }, [elementsToRender?.length, freshItemsRef.current, showItems]);

  useEffect(() => {
    //AE: The idea with this change is to avoid having rendered elements on screen if the user has not clicked yet on the show button.
    setTimeout(() => {
      if (!!showItems && parliamentLiveSection) {
        if (freshItemsRef.current) {
          freshItemsRef.current.classList.add('scrolled');
          setFreshItemsHeight(0);
          setTimeout(() => {
            //update content
            setResults({ ...results, hits: results?.hits?.map((item) => ({ ...item, freshItem: false })) });
            freshItemsRef.current.classList.remove('scrolled');
            setShowItems(false);
          }, transitionDuration);
        }
      }
    }, 300);
  }, [showItems]);

  const handleScroll = (scrollValues) => {
    const { scrollTop, scrollHeight, clientHeight } = scrollValues;
    const scrolledToBottom = scrollTop === scrollHeight - clientHeight;
    if (scrolledToBottom && typeOfContent !== 'Events' && typeOfContent !== 'RealtimeUtterances') {
      load_more_results();
    }
  };

  const dispatchApiCall = async (source) => {
    try {
      setIsLoading(true);
      const type = createTypeOfResource(typeOfContent);
      let results = await apiCallPerContent({ type, sourceProp: source });
      if (results) {
        setResults(results?.embedWidgetsAllowed ? results : null);
        setSwitchingResults(false);
        setIsLoading(false);
      } else {
        if (!switchingResource.current) {
          setResults(null);
          setIsLoading(false);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };
  useEffect(() => {
    const source = new AbortController();
    if (!!topic || !!userId) {
      dispatchApiCall(source);
    }
    return () => {
      switchingResource.current = true;
      source.abort('Embed cancelled');
    };
  }, [typeOfContent, topic, userId]);

  return (
    <>
      {isLoading || switchingResults ? (
        <div className='skeleton-embed-container flex-grow-1 overflow-hidden pt-3'>
          <SkeletonKeyUpdates />
        </div>
      ) : (
        <div
          className='embed-padding flex-grow-1 pt-3 pb-1 embed-list position-relative'
          style={{ backgroundColor: background }}
        >
          {results === null && <FeedNoAvailable />}
          {elementsToRender?.length > 0 && (
            <>
              {parliamentLiveSection && (
                <button
                  className={`fresh-items-button embed-fresh-items-button general-button action-button rounded-button alerts-button z-max ${
                    freshElements?.length > 0 ? '' : 'fresh-items-button-hidden'
                  }`}
                  onClick={showNewItems}
                >
                  <span className='icon-arrow-top mr-2'></span>New remarks ({freshElements.length})
                </button>
              )}
              <CustomScrollbar
                className='simple-scrollbar'
                contentClass={'normalized-scroll-container mr-0'}
                maximalThumbYSize={100}
                onScroll={(scrollValues) => {
                  handleScroll(scrollValues);
                }}
                ref={scrollbarContainer}
              >
                {typeOfContent === 'Events' && <CalendarEmbed elements={results.hits} keywordListId={keywordListId} />}
                {typeOfContent !== 'Events' && (
                  <>
                    {parliamentLiveSection && showItems && (
                      <ul
                        ref={freshItemsRef}
                        className='policy-list px-0 items-list all-open-consultations fresh-items-list mb-0'
                        style={{
                          marginTop: `-${freshItemsHeight}px`,
                          transition: `margin-top ${transitionDuration}ms ease-out`,
                        }}
                      >
                        {resultsToUse.hits
                          ?.filter((item) => item?.freshItem)
                          ?.slice(0, 100)
                          .map((item, index, array) => {
                            return (
                              <ParliamentLiveItem
                                key={`item-${typeOfContent}-${index}`}
                                listRow={{
                                  listContext: { item, array, index, listView: true },
                                }}
                              />
                            );
                          })}
                      </ul>
                    )}
                    <ul className='policy-list px-1 all-open-consultations items-list pb-1'>
                      {elementsToRender?.map((item, index, array) => {
                        const explanations = results.explanations;
                        const itemOnclick = createFunctionOnClick(item);
                        const embedShowTags = false;
                        const itemFromWidget = true;
                        const listRow = {
                          listContext: { index, item, array },
                          feedOverrides: { explanations },
                          display: { notShowShare: true },
                          interactions: { itemOnclick },
                          external: { embedShowTags, itemFromWidget },
                          sectionMeta: {},
                        };
                        return (
                          <ListItem
                            key={`item-priority-updates-${index}`}
                            sectionName={typeOfContent}
                            index={index}
                            listRow={listRow}
                            item={item}
                            keywordListId={keywordListId}
                            embed={true}
                          />
                        );
                      })}
                    </ul>
                  </>
                )}
              </CustomScrollbar>
            </>
          )}

          {elementsToRender?.length === 0 && (
            <div className='flex-grow-1'>
              <FeedNoAvailable noResults includeBranding={includeBrandingProperty} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EmbedList;
